import type { MouseEventHandler, ReactNode } from 'react';
import { forwardRef } from 'react';
import Highlighter from 'react-highlight-words';
import type { FlexProps, TableCellProps } from '@chakra-ui/react';
import { Box, Button, Flex, Td, Text, useTheme } from '@chakra-ui/react';

import { BadgeImportant } from '../../badge';
import { Dot } from '../../Dot/Dot';
import { FileIcon } from '../../icon';

interface Props {
  name: string;
  fileIconColor: string;
  titleColor?: string;
  textToHightlight?: string;
  tableCellProps?: TableCellProps;
  tableCellContentProps?: FlexProps;
  afterName?: ReactNode;
  searchValue?: string;
  children?: ReactNode;
  fileExtension?: string;
  showUnseen?: boolean;
  isFolder?: boolean;
  isLockedFolder?: boolean;
  isCertified?: boolean;
  isImportant?: boolean;
  isPending?: boolean;
  isSeen?: boolean;
  checkMarkComponent?: React.ReactElement;
  withFodFinIcon?: boolean;
  withNotaryIcon?: boolean;
  withCheckmark?: boolean;
  onClick?: MouseEventHandler;
  onDoubleClick?: MouseEventHandler;
}

const CheckMark = () => (
  <Box
    bg='white'
    position='absolute'
    borderRadius='full'
    top='7px'
    left='-6px'
    width={3}
    height={3}>
    <svg viewBox='0 0 14 15' fill='none'>
      <circle cx={7} cy={7.5} r={6} fill='#0F9F37' stroke='#fff' strokeWidth={2} />
      <path d='m4 7 2.308 2.5L10 5.5' stroke='#fff' strokeWidth={1.5} />
    </svg>
  </Box>
);

export const NameCell = forwardRef<HTMLTableCellElement, Props>(
  (
    {
      name,
      textToHightlight,
      afterName,
      fileExtension,
      fileIconColor,
      titleColor,
      isSeen,
      isPending,
      isCertified,
      isImportant,
      isFolder,
      isLockedFolder,
      withCheckmark,
      withFodFinIcon,
      withNotaryIcon,
      children,
      searchValue,
      showUnseen = true,
      tableCellProps,
      tableCellContentProps,
      onClick,
      onDoubleClick,
    },
    ref,
  ) => {
    const theme = useTheme();

    return (
      <Td ref={ref} onClick={onClick} onDoubleClick={onDoubleClick} {...tableCellProps}>
        <Flex alignItems='center' gap={4} {...tableCellContentProps}>
          <Flex shrink={0} pos='relative'>
            <FileIcon
              isFolder={isFolder}
              extension={fileExtension ?? ''}
              color={fileIconColor}
              withFodFinIcon={withFodFinIcon}
              isThirdParty={false}
              withNotaryIcon={withNotaryIcon}
              isCertified={isCertified}
              isLockedFolder={isLockedFolder}
            />
            {withCheckmark && <CheckMark />}
            {isPending && <Dot variant='secondary' pos='absolute' bottom='-2px' right='-2px' />}
          </Flex>
          <Flex direction='column'>
            <Button
              as={onClick || onDoubleClick ? undefined : 'div'}
              onClick={onClick}
              onDoubleClick={onDoubleClick}
              title={name}
              variant='unstyled'
              whiteSpace='inherit'
              textAlign='left'
              display='flex'
              flexDirection='row'
              gap={2}
              alignItems='center'
              justifyContent='start'>
              <Text
                variant={(showUnseen && !isSeen && 'tableHeadBold') || 'tableHead'}
                noOfLines={1}
                wordBreak='break-all'
                color={titleColor}>
                <Highlighter
                  autoEscape
                  searchWords={[searchValue || '']}
                  textToHighlight={textToHightlight || name}
                  highlightStyle={{
                    borderRadius: '3px',
                    backgroundColor: theme.colors.primary100,
                    color: theme.colors.primary500,
                    paddingInline: '2px',
                  }}
                />
              </Text>
              {afterName}
            </Button>
            {children}
          </Flex>
          {isImportant && <BadgeImportant />}
        </Flex>
      </Td>
    );
  },
);
